import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Form,
  Input,
  List,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
  Typography,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  BellOutlined,
  CheckCircleOutlined,
  CloudOutlined,
  DeleteOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  GlobalOutlined,
  InfoCircleOutlined,
  LockOutlined,
  MailOutlined,
  MobileOutlined,
  SafetyCertificateOutlined,
  SkinOutlined,
} from "@ant-design/icons";
import apiClient from "utils/apiClient";
import { useAuth } from "contexts/AuthContext";
import paths from "routes/paths";
import { OLIVEEB_COLORS } from "workspaces/colors";

const { Paragraph, Text, Title } = Typography;

const REASONS = [
  { label: "Privacy concerns", value: "PRIVACY_CONCERNS" },
  { label: "Too many notifications", value: "TOO_MANY_NOTIFICATIONS" },
  { label: "Not using the app enough", value: "NOT_USING_APP" },
  { label: "Found an alternative service", value: "FOUND_ALTERNATIVE" },
  { label: "Temporary break", value: "TEMPORARY_BREAK" },
  { label: "Technical issues", value: "TECHNICAL_ISSUES" },
  { label: "Safety or trust concerns", value: "SAFETY_CONCERNS" },
  { label: "Poor experience", value: "POOR_EXPERIENCE" },
  { label: "Other", value: "OTHER" },
];

const pageStyle = {
  maxWidth: 980,
  margin: "0 auto",
  padding: "18px 0 32px",
};

const cardStyle = {
  borderRadius: 8,
  borderColor: OLIVEEB_COLORS.oliveebGreenSoft,
};

const softCardStyle = {
  ...cardStyle,
  background: "linear-gradient(135deg, #F9FFFB 0%, #FFFFFF 100%)",
};

const primaryButtonStyle = {
  background: OLIVEEB_COLORS.oliveebGreen,
  borderColor: OLIVEEB_COLORS.oliveebGreen,
  fontWeight: 800,
};

const parseApiError = (error, fallback) =>
  error?.response?.data?.humanMessage ||
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

const mobileMatchedRequestConfig = (config = {}) => ({
  ...config,
  headers: {
    ...(config.headers || {}),
    "O-SKIP-WORKSPACE-CONTEXT": "1",
  },
});

const titleCase = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatLocation = (item) => {
  const city = titleCase(item?.city);
  const county = titleCase(item?.county);
  const state = String(item?.state || "").trim().toUpperCase();
  const country = String(item?.country || "").trim().toUpperCase();
  const hasCountryOnly = country && !city && !county && !state;
  const line1 = hasCountryOnly
    ? "Supported countrywide"
    : [city, county ? `${county} County` : "", state].filter(Boolean).join(", ");
  return {
    label: hasCountryOnly
      ? `${line1} - ${country}`
      : `${line1 || "Unknown location"} - ${country || "UNKNOWN COUNTRY"}`,
    countrywide: Boolean(hasCountryOnly),
  };
};

const strengthScore = (password = "") => {
  let score = 0;
  if (password.length >= 8) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/[a-z]/.test(password)) score += 1;
  if (/\d/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;
  return Math.min(score, 4);
};

function SettingsPageShell({ icon, title, subtitle, children }) {
  const navigate = useNavigate();
  return (
    <div className="project-page" style={pageStyle}>
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <Space align="start" style={{ justifyContent: "space-between", width: "100%" }}>
          <Space align="start" size={12}>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(paths.settings)}>
              Settings
            </Button>
            <div>
              <Space size={10}>
                <span style={{ color: OLIVEEB_COLORS.oliveebGreen, fontSize: 22 }}>{icon}</span>
                <Title level={3} style={{ margin: 0, color: OLIVEEB_COLORS.oliveebDarkGreen }}>
                  {title}
                </Title>
              </Space>
              <Paragraph style={{ margin: "6px 0 0", color: OLIVEEB_COLORS.oliveebBlue }}>
                {subtitle}
              </Paragraph>
            </div>
          </Space>
        </Space>
        {children}
      </Space>
    </div>
  );
}

export function ChangePasswordSettingsPage() {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const newPassword = Form.useWatch("newPassword", form) || "";
  const score = strengthScore(newPassword);
  const labels = ["Too weak", "Weak", "Fair", "Good", "Strong"];
  const colors = ["#c0392b", "#e67e22", "#f1c40f", "#27ae60", "#2ecc71"];

  const submit = async (values) => {
    setSubmitting(true);
    try {
      await apiClient.put(
        "/identity-service/api/v1/auth/password",
        {
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        },
        mobileMatchedRequestConfig(),
      );
      form.resetFields();
      message.success("Your password has been updated.");
    } catch (error) {
      message.error(parseApiError(error, "Could not change your password."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SettingsPageShell
      icon={<LockOutlined />}
      title="Change Password"
      subtitle="Update your account password using the same password policy as the mobile app."
    >
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <Card style={cardStyle}>
            <Form form={form} layout="vertical" onFinish={submit}>
              <Form.Item
                label="Current Password"
                name="currentPassword"
                rules={[{ required: true, message: "Enter your current password" }]}
              >
                <Input.Password autoComplete="current-password" />
              </Form.Item>
              <Form.Item
                label="New Password"
                name="newPassword"
                rules={[
                  { required: true, message: "Enter a new password" },
                  { min: 8, message: "New password must be at least 8 characters" },
                  () => ({
                    validator(_, value) {
                      if (!value) return Promise.resolve();
                      const classes = [
                        /[A-Z]/.test(value),
                        /[a-z]/.test(value),
                        /\d/.test(value),
                        /[^A-Za-z0-9]/.test(value),
                      ].filter(Boolean).length;
                      return classes >= 3
                        ? Promise.resolve()
                        : Promise.reject(new Error("Use at least 3 of: uppercase, lowercase, number, symbol."));
                    },
                  }),
                ]}
              >
                <Input.Password autoComplete="new-password" />
              </Form.Item>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6, marginTop: -8, marginBottom: 6 }}>
                {[0, 1, 2, 3].map((index) => (
                  <span
                    key={index}
                    style={{
                      height: 6,
                      borderRadius: 8,
                      background: index < score ? colors[score] : "#E5E7EB",
                    }}
                  />
                ))}
              </div>
              <Text style={{ color: colors[score], fontWeight: 700 }}>{labels[score]}</Text>
              <Form.Item
                label="Confirm New Password"
                name="confirmPassword"
                dependencies={["newPassword"]}
                style={{ marginTop: 16 }}
                rules={[
                  { required: true, message: "Confirm your new password" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      return !value || getFieldValue("newPassword") === value
                        ? Promise.resolve()
                        : Promise.reject(new Error("New password and confirmation do not match."));
                    },
                  }),
                ]}
              >
                <Input.Password autoComplete="new-password" />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={submitting} style={primaryButtonStyle}>
                Save Password
              </Button>
            </Form>
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card style={softCardStyle}>
            <Text strong>Tips for a strong password</Text>
            <ul style={{ margin: "10px 0 0", paddingLeft: 18, color: OLIVEEB_COLORS.textMuted }}>
              <li>Avoid common words or personal info</li>
              <li>Use a phrase you can remember</li>
              <li>Do not reuse passwords across apps</li>
            </ul>
          </Card>
        </Col>
      </Row>
    </SettingsPageShell>
  );
}

export function AppDetailsSettingsPage() {
  const { user, authToken } = useAuth();
  const clientId = typeof window !== "undefined" ? window.localStorage.getItem("clientId") : "";
  const appVersion = process.env.REACT_APP_VERSION || "N/A";
  const tokenIssuedAt = useMemo(() => {
    try {
      const payload = JSON.parse(atob(String(authToken || "").split(".")[1] || ""));
      return payload?.iat ? new Date(payload.iat * 1000).toLocaleString() : "N/A";
    } catch {
      return "N/A";
    }
  }, [authToken]);

  const details = [
    { label: "Client ID", value: clientId || "N/A" },
    { label: "Last Login", value: tokenIssuedAt },
    { label: "App Version", value: appVersion },
    { label: "Signed in as", value: user?.email || user?.username || "N/A" },
  ];

  return (
    <SettingsPageShell icon={<InfoCircleOutlined />} title="Olitask App Details" subtitle="Review client and app session details.">
      <Row gutter={[16, 16]}>
        {details.map((item) => (
          <Col xs={24} md={12} key={item.label}>
            <Card style={cardStyle}>
              <Text type="secondary">{item.label}</Text>
              <Text strong style={{ display: "block", marginTop: 6, color: OLIVEEB_COLORS.oliveebDarkBlue }}>
                {item.value}
              </Text>
            </Card>
          </Col>
        ))}
      </Row>
    </SettingsPageShell>
  );
}

export function SupportedLocationsSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");

  const load = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      setError("");
      const collected = [];
      let page = 0;
      let totalPages = 1;
      while (page < totalPages) {
        const response = await apiClient.get(
          "/api/v1/location-restrictions",
          mobileMatchedRequestConfig({
            params: { page, size: 100, sort: "createdAt,desc" },
          }),
        );
        const data = response.data || {};
        const content = Array.isArray(data) ? data : data.content || data.data || data.locations || [];
        collected.push(...content);
        totalPages = data.totalPages || 1;
        page += 1;
      }
      collected.sort((a, b) =>
        `${a.country || ""}|${a.state || ""}|${a.city || ""}|${a.county || ""}`.localeCompare(
          `${b.country || ""}|${b.state || ""}|${b.city || ""}|${b.county || ""}`,
        ),
      );
      setRows(collected);
    } catch (nextError) {
      setError(parseApiError(nextError, "Unable to load supported locations."));
      setRows([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load(false);
  }, []);

  const filteredRows = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return rows;
    return rows.filter((item) =>
      `${item.country || ""} ${item.state || ""} ${item.county || ""} ${item.city || ""}`.toLowerCase().includes(normalized),
    );
  }, [query, rows]);

  return (
    <SettingsPageShell icon={<GlobalOutlined />} title="Supported Locations" subtitle="Search active Olitask coverage locations.">
      <Card style={softCardStyle}>
        <Space direction="vertical" size={14} style={{ width: "100%" }}>
          <Space style={{ justifyContent: "space-between", width: "100%" }} wrap>
            <Tag color="blue">Active Coverage</Tag>
            <Button loading={refreshing} onClick={() => load(true)}>
              Refresh
            </Button>
          </Space>
          <Title level={4} style={{ margin: 0, color: OLIVEEB_COLORS.oliveebGreen }}>
            Where Olitask Is Available
          </Title>
          <Text style={{ color: OLIVEEB_COLORS.oliveebBlue }}>
            {filteredRows.length} active location{filteredRows.length === 1 ? "" : "s"}
          </Text>
          {error ? <Alert type="error" showIcon message={error} /> : null}
          <Input.Search
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search country, state, county, city..."
            allowClear
          />
        </Space>
      </Card>
      {loading ? (
        <Card style={cardStyle}>
          <Spin /> <Text style={{ marginLeft: 8 }}>Loading supported locations...</Text>
        </Card>
      ) : (
        <Row gutter={[12, 12]}>
          {filteredRows.map((item, index) => {
            const formatted = formatLocation(item);
            return (
              <Col xs={24} md={12} key={item.id || index}>
                <Card size="small" style={cardStyle}>
                  <Space>
                    {formatted.countrywide ? <Tag color="cyan">COUNTRYWIDE</Tag> : null}
                    <Text strong>{formatted.label}</Text>
                  </Space>
                </Card>
              </Col>
            );
          })}
          {!filteredRows.length ? (
            <Col span={24}>
              <Card style={cardStyle}>
                <Text type="secondary">{query ? "No matching locations." : "No active locations found."}</Text>
              </Card>
            </Col>
          ) : null}
        </Row>
      )}
    </SettingsPageShell>
  );
}

function VerificationPage({ type }) {
  const isPhone = type === "phone";
  const { user, refreshAuth } = useAuth();
  const [phone, setPhone] = useState(user?.phone || user?.mobilePhone || "");
  const [otpToken, setOtpToken] = useState("");
  const [maskedDestination, setMaskedDestination] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(isPhone ? user?.phoneVerified : user?.emailVerified);

  const start = async () => {
    setLoading(true);
    try {
      const response = await apiClient.post(
        `/identity-service/api/v1/auth/verification/${isPhone ? "phone" : "email"}/start`,
        isPhone && phone ? { phone } : {},
        mobileMatchedRequestConfig(),
      );
      setOtpToken(response.data?.otpToken || "");
      setMaskedDestination(response.data?.maskedDestination || "");
      message.success(`Verification code sent${response.data?.maskedDestination ? ` to ${response.data.maskedDestination}` : ""}.`);
    } catch (error) {
      message.error(parseApiError(error, `Failed to start ${type} verification.`));
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    if (!otpToken || !code.trim()) {
      message.warning("Start verification and enter the code first.");
      return;
    }
    setLoading(true);
    try {
      await apiClient.post(
        `/identity-service/api/v1/auth/verification/${isPhone ? "phone" : "email"}/verify`,
        {
          otpToken,
          code: code.trim(),
        },
        mobileMatchedRequestConfig(),
      );
      setVerified(true);
      setCode("");
      await refreshAuth?.();
      message.success(`${isPhone ? "Phone" : "Email"} verified.`);
    } catch (error) {
      message.error(parseApiError(error, `Failed to verify ${type}.`));
    } finally {
      setLoading(false);
    }
  };

  return (
    <SettingsPageShell
      icon={isPhone ? <MobileOutlined /> : <MailOutlined />}
      title={isPhone ? "Verify Phone" : "Verify Email"}
      subtitle={isPhone ? "Send a code to your phone and confirm ownership." : "Send a code to your email and confirm ownership."}
    >
      <Card style={cardStyle}>
        <Space direction="vertical" size={14} style={{ width: "100%" }}>
          {verified ? <Alert type="success" showIcon message={`${isPhone ? "Phone" : "Email"} is verified`} /> : null}
          {isPhone ? (
            <Input value={phone} onChange={(event) => setPhone(event.target.value)} placeholder="Phone number" />
          ) : (
            <Input value={user?.email || user?.username || ""} disabled placeholder="Email" />
          )}
          <Space wrap>
            <Button onClick={start} loading={loading} style={primaryButtonStyle} type="primary">
              Send Code
            </Button>
            {maskedDestination ? <Tag color="blue">{maskedDestination}</Tag> : null}
          </Space>
          <Input
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="Enter 6-digit code"
            inputMode="numeric"
            maxLength={6}
          />
          <Button onClick={verify} loading={loading} disabled={!otpToken} icon={<CheckCircleOutlined />}>
            Verify Code
          </Button>
        </Space>
      </Card>
    </SettingsPageShell>
  );
}

export function EmailVerificationSettingsPage() {
  return <VerificationPage type="email" />;
}

export function PhoneVerificationSettingsPage() {
  return <VerificationPage type="phone" />;
}

export function DeleteOrSuspendAccountSettingsPage() {
  const { user, logout } = useAuth();
  const [selectedAction, setSelectedAction] = useState("");
  const [selectedReason, setSelectedReason] = useState(null);
  const [customReason, setCustomReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const resolvedReason = useMemo(() => {
    if (!selectedReason) return null;
    if (selectedReason === "OTHER") return customReason.trim() || null;
    return REASONS.find((item) => item.value === selectedReason)?.label || selectedReason;
  }, [customReason, selectedReason]);

  const submit = () => {
    if (!selectedAction) {
      message.warning("Choose whether to suspend or delete your account.");
      return;
    }

    const isDelete = selectedAction === "DELETE";
    Modal.confirm({
      title: isDelete ? "Confirm Account Deletion" : "Confirm Account Suspension",
      content: isDelete
        ? "This submits an account deletion request. Once successful, you will be logged out."
        : "This submits an account suspension request. If you later want to unsuspend your account, contact Olitask admin at help@oliveeb.com.",
      okText: isDelete ? "Confirm Delete" : "Confirm Suspend",
      okButtonProps: { danger: isDelete },
      onOk: async () => {
        setSubmitting(true);
        try {
          if (!user?.id && !user?.userId) throw new Error("User profile is missing.");
          await apiClient.post(
            isDelete
              ? "/identity-service/api/v1/user-deletion-requests"
              : "/identity-service/api/v1/user-suspension-requests",
            isDelete
              ? { deletionReason: resolvedReason || null }
              : { suspensionReason: resolvedReason || null },
            mobileMatchedRequestConfig(),
          );
          message.success(isDelete ? "Delete request submitted." : "Suspend request submitted.");
          await logout(false, paths.simpleLogin);
        } catch (error) {
          message.error(parseApiError(error, "We could not process your request right now."));
        } finally {
          setSubmitting(false);
        }
      },
    });
  };

  return (
    <SettingsPageShell
      icon={<DeleteOutlined />}
      title="Deactivate or Delete Account"
      subtitle="Temporarily suspend your account or permanently request deletion."
    >
      <Card style={softCardStyle}>
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Alert
            type="warning"
            showIcon
            message="Account control"
            description="Suspension is temporary. Deletion is permanent and requests account and data removal."
          />
          <Radio.Group value={selectedAction} onChange={(event) => setSelectedAction(event.target.value)} style={{ width: "100%" }}>
            <Row gutter={[12, 12]}>
              <Col xs={24} md={12}>
                <Card size="small" style={selectedAction === "SUSPEND" ? { ...cardStyle, borderColor: OLIVEEB_COLORS.oliveebGreen } : cardStyle}>
                  <Radio value="SUSPEND">
                    <Text strong>Suspend Account</Text>
                    <Text type="secondary" style={{ display: "block" }}>Temporarily disable access and come back later.</Text>
                  </Radio>
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card size="small" style={selectedAction === "DELETE" ? { ...cardStyle, borderColor: OLIVEEB_COLORS.oliveebRed } : cardStyle}>
                  <Radio value="DELETE">
                    <Text strong>Delete Account</Text>
                    <Text type="secondary" style={{ display: "block" }}>Permanently request account removal and deletion handling.</Text>
                  </Radio>
                </Card>
              </Col>
            </Row>
          </Radio.Group>
          <Select
            value={selectedReason}
            onChange={setSelectedReason}
            allowClear
            placeholder="Select a reason (optional)"
            options={REASONS}
          />
          {selectedReason === "OTHER" ? (
            <Input.TextArea
              value={customReason}
              onChange={(event) => setCustomReason(event.target.value)}
              placeholder="Tell us more (optional)"
              maxLength={500}
              showCount
            />
          ) : null}
          <Card size="small" style={cardStyle}>
            <Text strong>{selectedAction === "DELETE" ? "Delete Account" : selectedAction === "SUSPEND" ? "Suspend Account" : "Manage Account"}</Text>
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">Reason: {resolvedReason || "Not provided"}</Text>
            </div>
          </Card>
          <Button danger={selectedAction === "DELETE"} type="primary" disabled={!selectedAction} loading={submitting} onClick={submit}>
            {selectedAction === "DELETE" ? "Continue to Delete" : selectedAction === "SUSPEND" ? "Continue to Suspend" : "Continue"}
          </Button>
        </Space>
      </Card>
    </SettingsPageShell>
  );
}

function SimplePreferencePage({ icon, title, subtitle, options, value, onChange }) {
  const [selected, setSelected] = useState(value);
  return (
    <SettingsPageShell icon={icon} title={title} subtitle={subtitle}>
      <Card style={cardStyle}>
        <Radio.Group
          value={selected}
          onChange={(event) => {
            setSelected(event.target.value);
            onChange?.(event.target.value);
          }}
          style={{ width: "100%" }}
        >
          <Space direction="vertical" size={10} style={{ width: "100%" }}>
            {options.map((option) => (
              <Card size="small" key={option} style={cardStyle}>
                <Radio value={option}>{option}</Radio>
              </Card>
            ))}
          </Space>
        </Radio.Group>
      </Card>
    </SettingsPageShell>
  );
}

export function NotificationsSettingsPage() {
  const { user } = useAuth();
  const helperId = user?.id || user?.userId || user?.helperId || "";
  const isHelper = String(user?.currentAppRole || "").toUpperCase() === "HELPER";
  const [notifyPush, setNotifyPush] = useState(true);
  const [notifyEmail, setNotifyEmail] = useState(false);
  const [notifyChat, setNotifyChat] = useState(true);
  const [categories, setCategories] = useState([]);
  const [categoryPrefs, setCategoryPrefs] = useState({});
  const [catLoading, setCatLoading] = useState(false);
  const [saving, setSaving] = useState({});

  const loadCategories = async () => {
    if (!isHelper) return;
    setCatLoading(true);
    try {
      const response = await apiClient.get(
        "/olitask-service/api/v1/task-categories/roots",
        mobileMatchedRequestConfig(),
      );
      const data = Array.isArray(response.data) ? response.data : response.data?.content || response.data?.data || [];
      setCategories(data);
      setCategoryPrefs((current) => {
        const next = { ...current };
        data.forEach((category) => {
          if (next[category.id] === undefined) next[category.id] = false;
        });
        return next;
      });
    } catch (error) {
      message.error(parseApiError(error, "Failed to load task category alerts."));
    } finally {
      setCatLoading(false);
    }
  };

  const loadCategoryPreference = async (categoryId) => {
    if (!helperId || !categoryId) return;
    try {
      const response = await apiClient.get(
        `/olitask-service/api/v1/helper-category-notifications/helper/${helperId}/category/${categoryId}`,
        mobileMatchedRequestConfig(),
      );
      setCategoryPrefs((current) => ({ ...current, [categoryId]: Boolean(response.data?.enabled) }));
    } catch {
      setCategoryPrefs((current) => ({ ...current, [categoryId]: false }));
    }
  };

  const saveCategoryPreference = async (categoryId, enabled) => {
    if (!helperId || !categoryId) return;
    setSaving((current) => ({ ...current, [categoryId]: true }));
    try {
      const response = await apiClient.put(
        `/olitask-service/api/v1/helper-category-notifications/helper/${helperId}/category/${categoryId}`,
        { enabled },
        mobileMatchedRequestConfig(),
      );
      setCategoryPrefs((current) => ({ ...current, [categoryId]: Boolean(response.data?.enabled) }));
    } catch (error) {
      setCategoryPrefs((current) => ({ ...current, [categoryId]: !enabled }));
      message.error(parseApiError(error, "Could not update notification preference."));
    } finally {
      setSaving((current) => ({ ...current, [categoryId]: false }));
    }
  };

  useEffect(() => {
    loadCategories();
  }, [isHelper]);

  useEffect(() => {
    if (!isHelper || !helperId || !categories.length) return;
    categories.forEach((category) => loadCategoryPreference(category.id));
  }, [categories, helperId, isHelper]);

  const allCategoriesEnabled =
    categories.length > 0 && categories.every((category) => Boolean(categoryPrefs[category.id]));

  const toggleAllCategories = async (enabled) => {
    const previous = { ...categoryPrefs };
    const next = {};
    categories.forEach((category) => {
      next[category.id] = enabled;
    });
    setCategoryPrefs(next);
    try {
      await Promise.all(categories.map((category) => saveCategoryPreference(category.id, enabled)));
    } catch {
      setCategoryPrefs(previous);
    }
  };

  return (
    <SettingsPageShell icon={<BellOutlined />} title="Notifications" subtitle="Manage notification preferences.">
      <Card style={cardStyle}>
        <Space direction="vertical" size={14} style={{ width: "100%" }}>
          <Space style={{ justifyContent: "space-between", width: "100%" }}>
            <Text strong>All Notifications</Text>
            <Switch checked={notifyPush} onChange={setNotifyPush} />
          </Space>
          <Space style={{ justifyContent: "space-between", width: "100%" }}>
            <Text strong>Email Notifications</Text>
            <Switch checked={notifyEmail} onChange={setNotifyEmail} />
          </Space>
          <Space style={{ justifyContent: "space-between", width: "100%" }}>
            <Text strong>Chat Notifications</Text>
            <Switch checked={notifyChat} onChange={setNotifyChat} />
          </Space>
          {isHelper ? (
            <>
              <div style={{ height: 1, background: OLIVEEB_COLORS.line }} />
              <Text strong>Task Category Alerts</Text>
              <Space style={{ justifyContent: "space-between", width: "100%" }}>
                <Text>Enable All Categories</Text>
                <Switch
                  checked={allCategoriesEnabled}
                  onChange={toggleAllCategories}
                  disabled={!categories.length || catLoading}
                />
              </Space>
              {catLoading ? (
                <Spin />
              ) : (
                categories.map((category) => (
                  <Space key={category.id} style={{ justifyContent: "space-between", width: "100%" }}>
                    <Text>{category.name}</Text>
                    <Switch
                      checked={Boolean(categoryPrefs[category.id])}
                      loading={Boolean(saving[category.id])}
                      onChange={(enabled) => {
                        const previous = Boolean(categoryPrefs[category.id]);
                        setCategoryPrefs((current) => ({ ...current, [category.id]: enabled }));
                        saveCategoryPreference(category.id, enabled).catch(() => {
                          setCategoryPrefs((current) => ({ ...current, [category.id]: previous }));
                        });
                      }}
                    />
                  </Space>
                ))
              )}
            </>
          ) : null}
        </Space>
      </Card>
    </SettingsPageShell>
  );
}

export function LanguageSettingsPage() {
  return (
    <SimplePreferencePage
      icon={<GlobalOutlined />}
      title="Language"
      subtitle="Choose your app language."
      options={["English"]}
      value="English"
    />
  );
}

export function ThemeSettingsPage() {
  return (
    <SimplePreferencePage
      icon={<SkinOutlined />}
      title="Theme"
      subtitle="Choose your visual theme."
      options={["Light"]}
      value="Light"
    />
  );
}

export function DataStorageSettingsPage() {
  return (
    <SimplePreferencePage
      icon={<CloudOutlined />}
      title="Data & Storage"
      subtitle="Choose retention and storage preferences."
      options={["N/A"]}
      value="N/A"
    />
  );
}

const securityStyles = {
  shellCard: {
    ...cardStyle,
    background: "#F7FBF8",
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "12px 14px",
    borderRadius: 8,
    background: "#F2FBF5",
    border: "1px solid #D6EEDF",
    boxShadow: "0 4px 10px rgba(39, 174, 96, 0.08)",
  },
  badge: {
    minWidth: 28,
    height: 28,
    borderRadius: 14,
    background: "#27ae60",
    color: "#fff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 8px",
    border: "1px solid #BDE8CC",
    fontWeight: 700,
    boxShadow: "0 3px 8px rgba(39, 174, 96, 0.18)",
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 900,
    color: "#0c3b2e",
  },
  card: {
    ...cardStyle,
    background: "#FCFFFD",
    borderColor: "#DCEDE3",
    boxShadow: "0 4px 12px rgba(26, 127, 73, 0.07)",
  },
  label: {
    fontSize: 13,
    fontWeight: 800,
    color: "#188A24",
  },
  browseButton: {
    padding: 0,
    height: "auto",
    color: "#2385DB",
    fontWeight: 800,
  },
  input: {
    borderColor: "#D6E7DA",
    borderRadius: 8,
    background: "#fff",
  },
  toggleOn: {
    borderColor: "#B9E0C4",
    background: "#EAF7EF",
    color: "#143d2e",
    fontWeight: 800,
  },
  toggleOff: {
    borderColor: "#d9dedb",
    background: "#fff",
    color: "#143d2e",
    fontWeight: 800,
  },
  hint: {
    color: "#c30707",
    fontSize: 12,
    textAlign: "center",
    lineHeight: "16px",
  },
  center: {
    minHeight: 180,
    display: "grid",
    placeItems: "center",
  },
};

export function SecurityQuestionsSettingsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [q1, setQ1] = useState(null);
  const [q2, setQ2] = useState(null);
  const [q3, setQ3] = useState(null);
  const [a1, setA1] = useState("");
  const [a2, setA2] = useState("");
  const [a3, setA3] = useState("");
  const [showA1, setShowA1] = useState(false);
  const [showA2, setShowA2] = useState(false);
  const [showA3, setShowA3] = useState(false);
  const [listModal, setListModal] = useState({ open: false, target: null });
  const [search, setSearch] = useState("");
  const [pwModalOpen, setPwModalOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);

  const normalized = (value) => String(value || "").trim().replace(/\s+/g, " ").toLowerCase();

  const pickerItems = useMemo(
    () => questions.map((question) => ({ label: question.questionText, value: question.id, key: question.id })),
    [questions],
  );

  const allShown = useMemo(() => showA1 && showA2 && showA3, [showA1, showA2, showA3]);

  const canSubmit = useMemo(() => {
    if (!q1 || !q2 || !q3) return false;
    if (q1 === q2 || q1 === q3 || q2 === q3) return false;
    return normalized(a1).length >= 2 && normalized(a2).length >= 2 && normalized(a3).length >= 2;
  }, [a1, a2, a3, q1, q2, q3]);

  const isDisabled = (id, self) => {
    if (self === "q1") return id === q2 || id === q3;
    if (self === "q2") return id === q1 || id === q3;
    return id === q1 || id === q2;
  };

  const selectedValue = (target) => {
    if (target === "q1") return q1;
    if (target === "q2") return q2;
    return q3;
  };

  const setSelectedValue = (target, value) => {
    if (target === "q1") setQ1(value);
    if (target === "q2") setQ2(value);
    if (target === "q3") setQ3(value);
  };

  const questionOptions = (target) =>
    pickerItems.map((item) => ({
      ...item,
      disabled: isDisabled(item.value, target),
    }));

  const filteredItems = useMemo(() => {
    const query = normalized(search);
    if (!query) return pickerItems;
    return pickerItems.filter((item) => normalized(item.label).includes(query));
  }, [pickerItems, search]);

  const openListFor = (target) => {
    setSearch("");
    setListModal({ open: true, target });
  };

  const selectFromList = (value) => {
    if (!listModal.target || isDisabled(value, listModal.target)) return;
    setSelectedValue(listModal.target, value);
    setListModal({ open: false, target: null });
  };

  const loadQuestions = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(
        "/identity-service/api/v1/security-questions",
        mobileMatchedRequestConfig({
          params: { active: true, page: 0, size: 200 },
        }),
      );
      const data = response.data || {};
      const content = Array.isArray(data?.content) ? data.content : Array.isArray(data) ? data : [];
      setQuestions(content.filter((question) => question.active));
    } catch (error) {
      message.error(parseApiError(error, "Failed to load security questions."));
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQuestions();
  }, []);

  const onPressSave = () => {
    if (!canSubmit) {
      Modal.warning({
        title: "Incomplete",
        content: "Please choose three different questions and provide answers (min 2 characters).",
      });
      return;
    }
    setPassword("");
    setShowPw(false);
    setPwModalOpen(true);
  };

  const submitWithPassword = async () => {
    if (!password || password.length < 2) {
      Modal.warning({
        title: "Password required",
        content: "Please enter your current account password to confirm.",
      });
      return;
    }

    setSaving(true);
    try {
      await apiClient.put(
        "/identity-service/api/v1/auth/security-questions",
        {
          question1Id: q1,
          answer1: a1,
          question2Id: q2,
          answer2: a2,
          question3Id: q3,
          answer3: a3,
          password,
        },
        mobileMatchedRequestConfig(),
      );
      setPwModalOpen(false);
      Modal.success({
        title: "Success",
        content: "Your security questions were updated.",
        onOk: () => navigate(-1),
      });
    } catch (error) {
      if (error?.response?.status === 400) {
        Modal.error({
          title: "Unable to save",
          content: parseApiError(
            error,
            "There was a problem with your request. Please review and try again.",
          ),
        });
      } else {
        Modal.error({
          title: "Error",
          content: parseApiError(error, "Failed to save your security questions."),
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const answerField = (label, value, setValue, hidden, setHidden) => (
    <>
      <Text style={{ ...securityStyles.label, display: "block", marginTop: 12 }}>{label}</Text>
      <Input
        value={value}
        onChange={(event) => setValue(event.target.value)}
        placeholder="Type your answer"
        type={hidden ? "password" : "text"}
        autoComplete="new-password"
        style={securityStyles.input}
        suffix={
          <Button
            type="text"
            size="small"
            icon={hidden ? <EyeOutlined /> : <EyeInvisibleOutlined />}
            onClick={() => setHidden((current) => !current)}
          />
        }
      />
    </>
  );

  const questionCard = (target, label, answerValue, setAnswerValue, isAnswerShown, setAnswerShown) => {
    const hidden = !isAnswerShown;
    return (
      <Card style={securityStyles.card} bodyStyle={{ padding: 12 }}>
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          <Space style={{ justifyContent: "space-between", width: "100%" }}>
            <Text style={securityStyles.label}>{label}</Text>
            <Button type="link" onClick={() => openListFor(target)} style={securityStyles.browseButton}>
              Browse all
            </Button>
          </Space>
          <Select
            value={selectedValue(target) || undefined}
            loading={loading}
            placeholder="Select a question..."
            options={questionOptions(target)}
            onChange={(value) => setSelectedValue(target, value)}
            style={{ width: "100%" }}
            showSearch
            optionFilterProp="label"
          />
          {answerField(
            `Answer ${target.slice(1)}`,
            answerValue,
            setAnswerValue,
            hidden,
            setAnswerShown,
          )}
        </Space>
      </Card>
    );
  };

  return (
    <SettingsPageShell
      icon={<SafetyCertificateOutlined />}
      title="Manage Security Questions"
      subtitle="Choose three active security questions and confirm with your current password."
    >
      <Card style={securityStyles.shellCard} bodyStyle={{ padding: 14 }}>
        {loading ? (
          <div style={securityStyles.center}>
            <Spin size="large" />
            <Text style={{ marginTop: 12 }}>Loading security questions...</Text>
          </div>
        ) : (
          <Space direction="vertical" size={10} style={{ width: "100%" }}>
            <div style={securityStyles.headerRow}>
              <Text style={securityStyles.sectionTitle}>Choose 3 Questions</Text>
              <span style={securityStyles.badge}>{questions.length}</span>
            </div>

            {questions.length === 0 ? (
              <Card style={securityStyles.card}>
                <Empty
                  description={
                    <Space direction="vertical" size={4}>
                      <Text strong>No active security questions available.</Text>
                      <Text type="secondary">Check your API seeding or the active=true filter.</Text>
                    </Space>
                  }
                />
              </Card>
            ) : (
              <>
                {questionCard("q1", "Question 1", a1, setA1, showA1, setShowA1)}
                {questionCard("q2", "Question 2", a2, setA2, showA2, setShowA2)}
                {questionCard("q3", "Question 3", a3, setA3, showA3, setShowA3)}
                <Row gutter={[10, 10]} align="middle">
                  <Col xs={24} sm={12}>
                    <Button
                      block
                      onClick={() => {
                        const targetState = !allShown;
                        setShowA1(targetState);
                        setShowA2(targetState);
                        setShowA3(targetState);
                      }}
                      style={allShown ? securityStyles.toggleOn : securityStyles.toggleOff}
                    >
                      {allShown ? "Hide all answers" : "Show all answers"}
                    </Button>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Button
                      block
                      type="primary"
                      onClick={onPressSave}
                      disabled={!canSubmit || saving}
                      loading={saving}
                      style={primaryButtonStyle}
                    >
                      Save
                    </Button>
                  </Col>
                </Row>
                <Text style={securityStyles.hint}>
                  Tip: choose questions you'll remember, and answers others can't guess.
                </Text>
              </>
            )}
          </Space>
        )}
      </Card>

      <Modal
        open={listModal.open}
        title="Browse questions"
        footer={null}
        onCancel={() => setListModal({ open: false, target: null })}
      >
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Input.Search
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search questions..."
            allowClear
          />
          <List
            dataSource={filteredItems}
            locale={{ emptyText: <Empty description="No matching questions." /> }}
            style={{ maxHeight: 420, overflow: "auto" }}
            renderItem={(item) => {
              const disabled = isDisabled(item.value, listModal.target || "q1");
              return (
                <List.Item
                  onClick={() => !disabled && selectFromList(item.value)}
                  style={{
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.45 : 1,
                    padding: "10px 4px",
                  }}
                >
                  <Text>{item.label}</Text>
                </List.Item>
              );
            }}
          />
        </Space>
      </Modal>

      <Modal
        open={pwModalOpen}
        title="Confirm with password"
        onCancel={() => setPwModalOpen(false)}
        okText="Confirm & Save"
        confirmLoading={saving}
        onOk={submitWithPassword}
      >
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Text type="secondary">
            For security, please enter your current account password to save changes.
          </Text>
          <Input
            placeholder="Current password"
            type={showPw ? "text" : "password"}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            suffix={
              <Button
                type="text"
                size="small"
                icon={showPw ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                onClick={() => setShowPw((current) => !current)}
              />
            }
          />
        </Space>
      </Modal>
    </SettingsPageShell>
  );
}
